import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TextFieldComponent from "components/input/TextFieldComponent";
import { MESSAGE } from "constants/validate";
import { formatCurrency } from "utils";
import { MstProcFeeTireItem } from "features/mst_proc_fee/types/proc-fee";
import { ChangeEvent, useMemo } from "react";
import {
  CASHBACK_PERIOD_OPTIONS,
  PUNCTURE_COMPENSATION_OPTIONS,
} from "features/mst_proc_fee/constants/options";

const StyledTableCell = styled(TableCell)({
  border: "1px solid white",
  textAlign: "center",
  fontWeight: "bold",
  backgroundColor: "var(--primary-500)",
  width: "calc((100% - 8px) / 5)",
});

const StyledBodyTableCell = styled(TableCell)({
  textAlign: "center",
  width: "210px",
  padding: "11px 20px",
});

// Constants
const VALIDATION_OPTIONS = {
  maxLength: {
    message: MESSAGE.MAX_LENGTH(9),
    value: 9,
  },
  halfWidth: {
    message: MESSAGE.HALFWIDTH,
  },
} as const;

const COMMON_INPUT_SX = {
  fontSize: { xs: 16, md: 18, lg: 20 },
  height: "48px",
  textAlign: "center" as const,
};

const COMMON_HEADER_SX = {
  py: "2px",
  fontSize: { xs: 16, md: 18, lg: 20 },
};

const COMMON_CHECKBOX_SX = {
  "& .MuiSvgIcon-root": { fontSize: { xs: 20, md: 28 } },
  padding: 0,
};

// Field configurations
interface FeeFieldConfig {
  key: keyof MstProcFeeTireItem;
  label: string;
  errorKey: string;
  hasCheckbox?: boolean;
  whiteSpace?: "nowrap";
  px?: number;
}

const FEE_FIELDS: FeeFieldConfig[] = [
  { key: "replac_labor_cost", label: "交換工賃", errorKey: "replac_labor_cost" },
  { key: "waste_tire_cost", label: "処分", errorKey: "waste_tire_cost", hasCheckbox: true },
  {
    key: "nitrogen_inflation_cost",
    label: "窒素",
    errorKey: "nitrogen_inflation_cost",
    hasCheckbox: true,
  },
  {
    key: "hub_rust_prevention_cost",
    label: "ハブ防錆",
    errorKey: "hub_rust_prevention_cost",
    hasCheckbox: true,
    whiteSpace: "nowrap",
  },
];

// Sub-components
interface HeaderCellWithCheckboxProps {
  label: string;
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  whiteSpace?: "nowrap";
  px?: number;
}

/**
 * Header cell component with checkbox for optional fee fields
 * @param props - Component props
 */
function HeaderCellWithCheckbox({
  label,
  checked,
  onChange,
  whiteSpace,
  px,
}: HeaderCellWithCheckboxProps) {
  return (
    <StyledTableCell
      sx={{
        border: "1px solid",
        backgroundColor: checked ? "var(--green-600)" : "var(--offwhite-500)",
        borderColor: checked ? "var(--green-600)" : "var(--offwhite-500)",
        whiteSpace,
        px,
        ...COMMON_HEADER_SX,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Checkbox checked={checked} onChange={onChange} sx={COMMON_CHECKBOX_SX} />
        {label}
      </Box>
    </StyledTableCell>
  );
}

interface FeeInputCellProps {
  value: number | undefined;
  fieldName: keyof MstProcFeeTireItem;
  errorKey: string;
  hasError: boolean;
  onChangeInput: (e: ChangeEvent<HTMLInputElement>, fieldName: keyof MstProcFeeTireItem) => void;
  onError: (field: string, error: boolean) => void;
  showInputDisable?: boolean;
}

/**
 * Input cell component for fee fields
 * @param props - Component props
 */
function FeeInputCell({
  value,
  fieldName,
  errorKey,
  hasError,
  onChangeInput,
  onError,
  showInputDisable = false,
}: FeeInputCellProps) {
  return (
    <StyledBodyTableCell sx={{ p: 0 }}>
      <TextFieldComponent
        type="string"
        value={formatCurrency(value as number)}
        sxInput={COMMON_INPUT_SX}
        {...(showInputDisable && {
          sxInputDisable: {
            color: "var(--black)",
            WebkitTextFillColor: "var(--black)",
          },
        })}
        sxRoot={{
          borderRadius: 0,
          width: "100%",
          ...(showInputDisable && { textAlign: "center", color: "var(--black)" }),
          border: !hasError ? "2px solid var(--silver-100)" : "2px solid var(--red-800)",
        }}
        sxNotchedOutline={{ border: "none" }}
        validateOption={VALIDATION_OPTIONS}
        isHiddenError
        onInputText={(e) => onChangeInput(e, fieldName)}
        onError={(error) => onError(errorKey, error)}
      />
    </StyledBodyTableCell>
  );
}

interface RadioGroupSectionProps {
  title: string;
  value: number;
  options: Array<{ label: string; value: number | string }>;
  onChange: (value: number) => void;
  disabled: boolean;
  width: { xs: string; md: string };
}

/**
 * Radio group section component for puncture compensation and cashback
 * @param props - Component props
 */
function RadioGroupSection({
  title,
  value,
  options,
  onChange,
  disabled,
  width,
}: RadioGroupSectionProps) {
  return (
    <Box sx={{ width }}>
      <Box
        sx={{
          fontSize: { xs: 16, md: 18, lg: 20 },
          fontWeight: "bold",
          height: "48px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "var(--offwhite-500)",
        }}
      >
        {title}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <RadioGroup
          row
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          sx={{ columnGap: "10px", paddingX: "10px" }}
        >
          {options.map((item) => (
            <FormControlLabel
              key={item.value}
              value={item.value}
              disabled={disabled}
              control={<Radio />}
              label={
                <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, color: "var(--black)" }}>
                  {item.label}
                </Typography>
              }
            />
          ))}
        </RadioGroup>
      </Box>
    </Box>
  );
}

interface ProcFeeTableProps {
  procFees?: MstProcFeeTireItem | null;
  isDisposalChecked: boolean;
  isValveChecked: boolean;
  isNitrogenChecked: boolean;
  isHubProtectionChecked: boolean;
  punctureCompensation: number;
  cashbackPeriod: number;
  tireNumber: number;
  errors: { [key: string]: boolean };
  onDisposalChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValveChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNitrogenChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHubProtectionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPunctureCompensationChange: (value: number) => void;
  onCashbackPeriodChange: (value: number) => void;
  onChangeInput: (e: ChangeEvent<HTMLInputElement>, fieldName: keyof MstProcFeeTireItem) => void;
  onError: (field: string, error: boolean) => void;
}

/**
 * Component to display and edit procedure fee settings
 * Includes labor costs, optional fees, puncture compensation, and cashback settings
 * @param props - Component props
 */
function ProcFeeTable({
  procFees,
  isDisposalChecked,
  isValveChecked,
  isNitrogenChecked,
  isHubProtectionChecked,
  punctureCompensation,
  cashbackPeriod,
  tireNumber,
  errors,
  onDisposalChange,
  onValveChange,
  onNitrogenChange,
  onHubProtectionChange,
  onPunctureCompensationChange,
  onCashbackPeriodChange,
  onChangeInput,
  onError,
}: ProcFeeTableProps) {
  // Map checkbox states and handlers to field keys
  const checkboxConfig = useMemo(
    () => ({
      waste_tire_cost: { checked: isDisposalChecked, onChange: onDisposalChange },
      rubber_stem_valve_cost: { checked: isValveChecked, onChange: onValveChange },
      nitrogen_inflation_cost: { checked: isNitrogenChecked, onChange: onNitrogenChange },
      hub_rust_prevention_cost: {
        checked: isHubProtectionChecked,
        onChange: onHubProtectionChange,
      },
    }),
    [
      isDisposalChecked,
      isValveChecked,
      isNitrogenChecked,
      isHubProtectionChecked,
      onDisposalChange,
      onValveChange,
      onNitrogenChange,
      onHubProtectionChange,
    ],
  );

  const isDisabled = tireNumber !== 4;

  return (
    <Box sx={{ width: { xs: "100%", xl: "calc((100% - 80px) *2 / 5)" } }}>
      {/* Header */}
      <Typography
        sx={{
          textAlign: "right",
          fontWeight: "medium",
          fontSize: { xs: "28px", md: "31px" },
          color: "var(--gray-700)",
          mb: 1.5,
        }}
      >
        工賃設定
      </Typography>
      <Typography
        sx={{
          color: "var(--gray-700)",
          textAlign: "right",
          fontWeight: "600",
          fontSize: { xs: 12, md: 14, lg: 16 },
        }}
      >
        1本あたりの税込金額
      </Typography>

      {/* Fee Table */}
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Table
          sx={{
            borderCollapse: "unset",
            borderSpacing: "2px",
            boxSizing: "border-box",
            minWidth: 695,
          }}
        >
          <TableHead>
            <TableRow sx={{ height: "48px" }}>
              {FEE_FIELDS.map((field) => {
                if (field.hasCheckbox) {
                  const config = checkboxConfig[field.key as keyof typeof checkboxConfig];
                  if (!config) return null;
                  return (
                    <HeaderCellWithCheckbox
                      key={field.key}
                      label={field.label}
                      checked={config.checked}
                      onChange={config.onChange}
                      whiteSpace={field.whiteSpace}
                      px={field.px}
                    />
                  );
                }
                return (
                  <StyledTableCell
                    key={field.key}
                    sx={{
                      backgroundColor: "var(--green-600)",
                      border: "1px solid var(--green-600)",
                      whiteSpace: "nowrap",
                      ...COMMON_HEADER_SX,
                    }}
                  >
                    {field.label}
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow>
              {FEE_FIELDS.map((field) => (
                <FeeInputCell
                  key={field.key}
                  value={procFees?.[field.key] as number | undefined}
                  fieldName={field.key}
                  errorKey={field.errorKey}
                  hasError={errors[field.errorKey]}
                  onChangeInput={onChangeInput}
                  onError={onError}
                  showInputDisable={field.key === "replac_labor_cost"}
                />
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* Puncture Compensation & Cashback */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "end",
          marginTop: "10px",
          columnGap: "4px",
        }}
      >
        <RadioGroupSection
          title="パンク補償"
          value={punctureCompensation}
          options={PUNCTURE_COMPENSATION_OPTIONS}
          onChange={onPunctureCompensationChange}
          disabled={isDisabled}
          width={{ xs: "100%", md: "calc(60% - 4px)" }}
        />
        <RadioGroupSection
          title="キャッシュバック"
          value={cashbackPeriod}
          options={CASHBACK_PERIOD_OPTIONS}
          onChange={onCashbackPeriodChange}
          disabled={isDisabled}
          width={{ xs: "100%", md: "40%" }}
        />
      </Box>
    </Box>
  );
}

export default ProcFeeTable;
